import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PointsInput } from "./pointsInput.component";
import type { KorrekturKorrekturbogenItem } from "../korrektur.types";

interface KorrekturbogenEditorProps {
	title: string;
	korrekturbogenItems: KorrekturKorrekturbogenItem[];
	musterloesungId: string;
	musterloesungPoints?: number | null;
	onPointsUpdate: (
		korrekturKorrekturbogenItemId: string,
		userPoints: number,
	) => void;
	onMusterloesungPointsUpdate: (
		musterloesungId: string,
		userPoints: number,
	) => void;
	readOnly?: boolean;
}

/**
 * KorrekturbogenEditor - A component that displays a list of korrekturbogen items so user can edit own points
 *
 * This component displays a list of korrekturbogen items, each with a title,
 * a list of parsed items, and a user point input for each item.
 *
 * @param {korrekturbogenItems} - The list of korrekturbogen items to display
 * @param {onUpdate} - A callback function that is called when the user points are updated
 * @param {readOnly} - Whether to show the component in read only mode
 * @returns {JSX.Element} The rendered component
 */
export default function KorrekturbogenEditor({
	korrekturbogenItems,
	title,
	musterloesungId,
	musterloesungPoints,
	onPointsUpdate,
	onMusterloesungPointsUpdate,
	readOnly = false,
}: KorrekturbogenEditorProps) {
	const [items, setItems] = useState<KorrekturKorrekturbogenItem[]>([]);
	const [accumulatedPoints, setAccumulatedPoints] = useState(0);
	const [totalPoints, setTotalPoints] = useState(0);
	const [musterloesungUserPoints, setMusterloesungUserPoints] =
		useState<number>(0);
	const [isItemsLocked, setIsItemsLocked] = useState<boolean>(false);
	const [isTitleLocked, setIsTitleLocked] = useState<boolean>(false);
	// Update local state when props change
	useEffect(() => {
		setItems(korrekturbogenItems);
	}, [korrekturbogenItems]);

	// Initialize musterloesung points from props
	useEffect(() => {
		setMusterloesungUserPoints(musterloesungPoints || 0);
	}, [musterloesungPoints]);

	// Calculate accumulated and total points, and determine locking states
	useEffect(() => {
		const total = items.reduce((sum, item) => {
			return sum + item.maxPoints;
		}, 0);

		// Check if any individual items have points assigned
		const hasIndividualPoints = items.some(
			(item) => (item.userPoints || 0) > 0,
		);

		// Check if musterloesung has points assigned
		const hasMusterloesungPoints = musterloesungUserPoints > 0;

		// Set locking states
		setIsItemsLocked(hasMusterloesungPoints);
		setIsTitleLocked(hasIndividualPoints);

		// Calculate accumulated points based on which mode is active
		let accumulated = 0;
		if (hasMusterloesungPoints && !hasIndividualPoints) {
			// Use musterloesung points if title has points and items don't
			accumulated = musterloesungUserPoints;
		} else {
			// Use individual item points (default behavior)
			accumulated = items.reduce((sum, item) => {
				const userPoints = item.userPoints || 0;
				return sum + userPoints;
			}, 0);
		}

		setAccumulatedPoints(accumulated);
		setTotalPoints(total);
	}, [items, musterloesungUserPoints]);

	// Function to handle userPoints changes
	const handleUserPointsChange = (
		korrekturKorrekturbogenItemId: string,
		newValue: number,
	) => {
		if (readOnly || isItemsLocked) return;
		try {
			// Update local state first for better responsiveness
			const updatedItems = items.map((item) =>
				item.id === korrekturKorrekturbogenItemId
					? { ...item, userPoints: newValue }
					: item,
			);
			setItems(updatedItems);
			onPointsUpdate(korrekturKorrekturbogenItemId, newValue);
		} catch (error) {
			console.error("Failed to update points:", error);
			toast.error("Fehler beim Speichern der Punkte", {
				description:
					error instanceof Error ? error.message : "Unbekannter Fehler",
			});
			// Optionally revert state if the update fails
			setItems(korrekturbogenItems);
		}
	};

	// Function to handle musterloesung points changes
	const handleMusterloesungPointsChange = (newValue: number) => {
		if (readOnly || isTitleLocked || !musterloesungId) return;
		try {
			setMusterloesungUserPoints(newValue);
			onMusterloesungPointsUpdate(musterloesungId, newValue);
		} catch (error) {
			console.error("Failed to update musterloesung points:", error);
			toast.error("Fehler beim Speichern der Punkte", {
				description:
					error instanceof Error ? error.message : "Unbekannter Fehler",
			});
			// Revert state if the update fails
			setMusterloesungUserPoints(musterloesungPoints || 0);
		}
	};

	// This function removes the font-size style from the content
	const formatContent = (content: string) => {
		try {
			// Use regex to remove font-size styles
			return content.replace(/font-size:\s*\d+(\.\d+)?(pt|px);?/g, "");
		} catch (error) {
			console.error("Error processing HTML:", error);
			return content; // Return original content if processing fails
		}
	};

	return (
		<div className="flex flex-col h-full overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
			<div className="flex flex-col bg-backgroundSecondary rounded-lg last:mb-0">
				<div className="px-3 py-2 flex justify-between items-center sticky top-0 bg-gray-200 z-5 pr-4 @container min-w-0">
					<h2 className="font-semibold truncate flex-1 min-w-0 mr-2">
						{title}
					</h2>
					<div className="flex flex-col @[400px]:flex-row items-end @[400px]:items-center gap-0.5 @[400px]:gap-2 shrink-0">
						{!readOnly && (
							<PointsInput
								value={
									isTitleLocked ? accumulatedPoints : musterloesungUserPoints
								}
								min={0}
								max={totalPoints}
								step={0.5}
								disabled={isTitleLocked}
								onChange={handleMusterloesungPointsChange}
								onClick={(e) => e.currentTarget.select()}
								className="w-16 p-1 font-semibold"
								title={
									isTitleLocked
										? "Gesperrt: Einzelne Punkte wurden vergeben"
										: "Gesamtpunkte eingeben"
								}
							/>
						)}
						{readOnly && (
							<div className="font-semibold">{accumulatedPoints}</div>
						)}
						<span className="text-muted-foreground text-xs">von</span>
						<div className="font-semibold">{totalPoints}</div>
					</div>
				</div>

				{/* Individual Items */}
				{items.map((korrekturbogenItem) => (
					<div
						className="p-3 border-b border-dashed last:border-b-0"
						key={korrekturbogenItem.id}
					>
						<div className="flex items-center justify-between @container min-w-0">
							<div
								className="font-medium leading-tight prose prose-xs max-w-none flex-1 mr-4 min-w-0 overflow-hidden"
								dangerouslySetInnerHTML={{
									__html: DOMPurify.sanitize(
										formatContent(korrekturbogenItem.text),
									),
								}}
							/>
							<div className="flex flex-col @[400px]:flex-row items-end @[400px]:items-center gap-0.5 @[400px]:gap-1 shrink-0">
								<div className="flex items-center gap-2">
									<PointsInput
										id={`userPoints-${korrekturbogenItem.id}`}
										value={
											korrekturbogenItem.userPoints !== undefined &&
											korrekturbogenItem.userPoints !== null
												? korrekturbogenItem.userPoints
												: ""
										}
										min={0}
										max={korrekturbogenItem.maxPoints}
										step={0.5}
										disabled={readOnly || isItemsLocked}
										onChange={(value) =>
											handleUserPointsChange(korrekturbogenItem.id, value)
										}
										onClick={(e) => e.currentTarget.select()}
										onKeyDown={(e) => {
											// Handle tabbing to next input
											if (e.key === "Tab") {
												// Let the default tab behavior work
												// It will automatically move to the next focusable element
											} else if (e.key === "Enter") {
												// Save on Enter key press
												const value =
													(e.target as HTMLInputElement).value === ""
														? 0
														: Number.parseFloat(
																(e.target as HTMLInputElement).value,
															);
												handleUserPointsChange(korrekturbogenItem.id, value);
											}
										}}
										className="w-12 p-0.5"
										title={
											isItemsLocked
												? "Gesperrt: Gesamtpunkte wurden vergeben"
												: ""
										}
										tabIndex={0}
									/>
								</div>
								<div className="flex items-center gap-1 justify-center">
									<span className="text-muted-foreground text-xs">von</span>
									<span className="font-medium min-w-6">
										{korrekturbogenItem.maxPoints}
									</span>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
